import { getOS } from '@/helpers/getOSDetails';
import { getBrowserDetails } from '@helpers/getBrowserDetails';
import { logs } from '@opentelemetry/api-logs';
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { W3CTraceContextPropagator } from '@opentelemetry/core';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { resourceFromAttributes } from '@opentelemetry/resources';
import type { LogRecordProcessor, SdkLogRecord } from '@opentelemetry/sdk-logs';
import { BatchLogRecordProcessor, LoggerProvider } from '@opentelemetry/sdk-logs';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import {
  SEMRESATTRS_DEPLOYMENT_ENVIRONMENT,
  SEMRESATTRS_SERVICE_NAME,
  SEMRESATTRS_SERVICE_VERSION,
} from '@opentelemetry/semantic-conventions';
import { useAuthStore } from '@stores/authStore';

const browserDetails = getBrowserDetails();
const osName = getOS();

/**
 * Which deployment this telemetry came from.
 *
 * Deliberately *not* VITE_MODE: stage and prod both build with `VITE_MODE=PROD`
 * (VITE_MODE gates behaviour, and stage must behave like prod), and both export to
 * the same collector at apm.therame.de — so keying telemetry on VITE_MODE made
 * stage and prod frontend records indistinguishable, and any alert on the prod
 * error rate would also fire on stage noise.
 *
 * Falls back to VITE_MODE so an env file that has not set it still reports
 * something. PROD keeps resolving to `prod_frontend`, which is the service_name
 * the existing Grafana dashboards query.
 */
const telemetryEnv = (import.meta.env.VITE_TELEMETRY_ENV ?? import.meta.env.VITE_MODE) as string;

const resource = resourceFromAttributes({
  [SEMRESATTRS_SERVICE_NAME]: telemetryEnv.toString().toLocaleLowerCase() + '_frontend',
  [SEMRESATTRS_SERVICE_VERSION]: APP_VERSION,
  [SEMRESATTRS_DEPLOYMENT_ENVIRONMENT]: telemetryEnv,
  ['browser.name']: browserDetails.browserName,
  ['browser.version']: browserDetails.fullVersion,
  ['os.name']: osName,
  ['user_agent.original']: navigator.userAgent,
});

// Custom LogRecordProcessor to add user context
class UserContextLogRecordProcessor implements LogRecordProcessor {
  forceFlush(): Promise<void> {
    return Promise.resolve();
  }

  onEmit(logRecord: SdkLogRecord): void {
    try {
      const authStore = useAuthStore();
      if (authStore.authContext?.user?.id) {
        logRecord.setAttribute('user.id', authStore.authContext.user.id);
        logRecord.setAttribute(
          'user.currentOfficeLocationId',
          authStore.authContext.user.currentOfficeLocationId,
        );
      }
    } catch {
      // Pinia might not be initialized yet, ignore
    }
  }

  shutdown(): Promise<void> {
    return Promise.resolve();
  }
}

if (import.meta.env.VITE_OTLP_ENDPOINT_HTTP) {
  const exporter = new OTLPTraceExporter({
    url: `${import.meta.env.VITE_OTLP_ENDPOINT_HTTP}/v1/traces`,
  });
  const provider = new WebTracerProvider({
    spanProcessors: [new BatchSpanProcessor(exporter)],
    resource,
  });

  provider.register({
    contextManager: new ZoneContextManager(),
    propagator: new W3CTraceContextPropagator(),
  });

  // Logger Provider
  const logExporter = new OTLPLogExporter({
    url: `${import.meta.env.VITE_OTLP_ENDPOINT_HTTP}/v1/logs`,
  });
  const loggerProvider = new LoggerProvider({
    resource,
    processors: [new BatchLogRecordProcessor(logExporter), new UserContextLogRecordProcessor()],
  });

  // Register the logger provider globally
  logs.setGlobalLoggerProvider(loggerProvider);

  // Registering instrumentations
  registerInstrumentations({
    instrumentations: [
      getWebAutoInstrumentations({
        '@opentelemetry/instrumentation-xml-http-request': {
          clearTimingResources: true,
          propagateTraceHeaderCorsUrls: [
            /^https:\/\/(?!auth).*\.therame\.(de|localhost)/,
            /^http:\/\/localhost/,
          ],
        },
        '@opentelemetry/instrumentation-fetch': {
          propagateTraceHeaderCorsUrls: [
            /^https:\/\/(?!auth).*\.therame\.(de|localhost)/,
            /^http:\/\/localhost/,
          ],
        },
      }),
    ],
  });
}
